import { HEADERS_JSON } from "@/common/api/constants";
import { CommonFetchRequest, CommonRequestProps } from "@/common/api/types";
import { responseHandler } from "@/common/api/utils";
import { API_URL } from "@/config";
import {
  LatestLoanRequest,
  LatestLoanResponse,
  LoanDocumentsRequest,
  PostDirectorEmailRequestProps,
  SignLoanRequestProps,
} from "@/entities/loan/api/types";
import { LoanDocuments, LoanInfoProps } from "@/entities/loan/model/types";

export const fetchLoanInfo = async ({
  setErrorMessage,
  token,
  leadId,
}: CommonRequestProps<{ token: string; leadId?: number }>) => {
  const response = await fetch(`${API_URL}/cabinet/loan/${leadId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...HEADERS_JSON,
    },
  });

  const handledResponse = await responseHandler({ response, setErrorMessage });
  return (
    (await handledResponse?.json()) as {
      data: LoanInfoProps | null;
    }
  )?.data;
};

export const fetchLoanDocuments = async ({
  setErrorMessage,
  token,
  loanId,
}: LoanDocumentsRequest): Promise<LoanDocuments | null | undefined> => {
  const response = await fetch(`${API_URL}/cabinet/loan/documents/${loanId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...HEADERS_JSON,
    },
  });
  const result = await responseHandler({ response, setErrorMessage });
  return ((await result?.json()) as { data: LoanDocuments | null })?.data;
};

export const fetchLatestLoan = async ({
  setErrorMessage,
  token,
  iin,
}: LatestLoanRequest): Promise<LatestLoanResponse> => {
  const response = await fetch(`${API_URL}/latest-active-loan/${iin}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...HEADERS_JSON,
    },
  });
  const result = await responseHandler({ response, setErrorMessage });
  return result?.json();
};

export const postSignLoan: CommonFetchRequest<{
  token: string;
  data: SignLoanRequestProps;
}> = async ({ token, data, setErrorMessage }) => {
  const response = await fetch(`${API_URL}/cabinet/sign-loan`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...HEADERS_JSON,
    },
    body: JSON.stringify(data),
  });
  return await responseHandler({ response, setErrorMessage });
};

export const postResendCode: CommonFetchRequest<{
  token: string;
  loanId: number;
}> = async ({ token, loanId, setErrorMessage }) => {
  const response = await fetch(`${API_URL}/cabinet/resend-loan-sign-code`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...HEADERS_JSON,
    },
    body: JSON.stringify({
      loan_id: loanId,
    }),
  });
  return await responseHandler({ response, setErrorMessage });
};

export const postDirectorEmail: CommonFetchRequest<{
  token: string;
  data: PostDirectorEmailRequestProps;
}> = async ({ token, data, setErrorMessage }) => {
  const response = await fetch(
    `${API_URL}/cabinet/loan/confirm-director-email`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        ...HEADERS_JSON,
      },
      body: JSON.stringify(data),
    },
  );
  if (response.status === 488) {
    return response;
  }
  return await responseHandler({ response, setErrorMessage });
};
